import React, { useEffect, useState } from 'react';
import client, { apiCall } from '../../api/client';
import DataTable from '../../components/DataTable';
import StatusBadge from '../../components/StatusBadge';
import Loader from '../../components/Loader';

const EMPTY_FORM = {
  name: '', description: '', price: 0, originalPrice: 0, durationInDays: 30,
  monthlyCredits: 1000000, dailyCreditCap: 100000, rateLimitPerSecond: 15, maxApiKeys: 1, allowedNetworks: [],
};

function NetworkCheckboxes({ networks, selected, onChange }) {
  const toggle = (slug) => {
    onChange(selected.includes(slug) ? selected.filter((s) => s !== slug) : [...selected, slug]);
  };
  return (
    <div className="network-checkbox-grid">
      {networks.map((n) => {
        const checked = selected.includes(n.slug);
        return (
          <label key={n.uniqueId} style={{ display: "flex" }} className={`network-checkbox-item ${checked ? 'checked' : ''}`}>
            <input type="checkbox" checked={checked} onChange={() => toggle(n.slug)} />
            <span style={{ marginLeft: 6 }}>{n.name}</span>
          </label>
        );
      })}
    </div>
  );
}

export default function Plans() {
  const [plans, setPlans] = useState([]);
  const [networks, setNetworks] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = () => {
    apiCall(client.get('/credit_plan/admin/get_list'))
      .then(setPlans)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
    apiCall(client.get('/network/admin/get_list')).then(setNetworks).catch(() => setNetworks([]));
  };
  useEffect(load, []);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const numericFields = ['price', 'originalPrice', 'durationInDays', 'monthlyCredits', 'dailyCreditCap', 'rateLimitPerSecond', 'maxApiKeys'];
  const toNumbers = (obj) => {
    const out = { ...obj };
    numericFields.forEach((f) => { out[f] = +out[f]; });
    return out;
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      await apiCall(client.post('/credit_plan/admin/create', toNumbers(form)));
      setForm(EMPTY_FORM);
      setShowAdd(false);
      load();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      await apiCall(client.put('/credit_plan/admin/update', toNumbers(editing)));
      setEditing(null);
      load();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (row) => {
    try {
      await apiCall(client.patch('/credit_plan/admin/update_status', { uniqueId: row.uniqueId, status: !row.status }));
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  const columns = [
    { key: 'name', label: 'Name' },
    { key: 'price', label: 'Price', render: (row) => `$${row.price}` },
    { key: 'monthlyCredits', label: 'Monthly credits', render: (row) => row.monthlyCredits.toLocaleString() },
    { key: 'dailyCreditCap', label: 'Daily credits', render: (row) => row.dailyCreditCap.toLocaleString() },
    { key: 'rateLimitPerSecond', label: 'RPS' },
    { key: 'maxApiKeys', label: 'Max RPC URLs', render: (row) => row.maxApiKeys || 1 },
    { key: 'allowedNetworks', label: 'Networks', render: (row) => row.allowedNetworks.length },
    { key: 'status', label: 'Status', render: (row) => <StatusBadge value={row.status} /> },
    {
      key: 'actions', label: '', render: (row) => (
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-outline btn-sm" onClick={() => setEditing({ ...row, maxApiKeys: row.maxApiKeys || 1 })}>Edit</button>
          <button className="btn btn-outline btn-sm" onClick={() => toggleStatus(row)}>
            {row.status ? 'Disable' : 'Enable'}
          </button>
        </div>
      )
    },
  ];

  return (
    <div>
      <div className="page-header-row">
        <h1>Plans</h1>
        <button className="btn btn-primary" onClick={() => { setForm(EMPTY_FORM); setShowAdd(true); }}>+ Add Plan</button>
      </div>
      {error && <div className="form-error">{error}</div>}

      <div className="card">
        {loading ? (
          <Loader label="Loading plans..." />
        ) : (
          <DataTable columns={columns} rows={plans} emptyMessage="No plans configured yet." />
        )}
      </div>

      {showAdd && (
        <div className="modal-backdrop" onClick={() => setShowAdd(false)}>
          <div className="modal-card" style={{ maxWidth: 520 }} onClick={(e) => e.stopPropagation()}>
            <h3>Add plan</h3>
            <form onSubmit={handleCreate}>
              <div className="form-row">
                <div className="form-group"><label>Name</label>
                  <input name="name" required value={form.name} onChange={handleChange} /></div>
                <div className="form-group" style={{ flex: 2 }}><label>Description</label>
                  <input name="description" value={form.description} onChange={handleChange} /></div>
              </div>
              <div className="form-row">
                <div className="form-group"><label>Price ($)</label>
                  <input name="price" type="number" min="0" required value={form.price} onChange={handleChange} /></div>
                <div className="form-group"><label>Original price ($)</label>
                  <input name="originalPrice" type="number" min="0" required value={form.originalPrice} onChange={handleChange} /></div>
                <div className="form-group"><label>Duration (days)</label>
                  <input name="durationInDays" type="number" min="1" required value={form.durationInDays} onChange={handleChange} /></div>
              </div>
              <div className="form-row">
                <div className="form-group"><label>Monthly credits</label>
                  <input name="monthlyCredits" type="number" min="0" required value={form.monthlyCredits} onChange={handleChange} /></div>
                <div className="form-group"><label>Daily credit cap</label>
                  <input name="dailyCreditCap" type="number" min="0" required value={form.dailyCreditCap} onChange={handleChange} /></div>
                <div className="form-group"><label>Rate limit (rps)</label>
                  <input name="rateLimitPerSecond" type="number" min="1" required value={form.rateLimitPerSecond} onChange={handleChange} /></div>
                <div className="form-group"><label>Max RPC URLs</label>
                  <input name="maxApiKeys" type="number" min="1" required value={form.maxApiKeys} onChange={handleChange} /></div>
              </div>
              <div className="form-group" style={{ marginBottom: 20 }}>
                <label>Allowed networks</label>
                <NetworkCheckboxes
                  networks={networks}
                  selected={form.allowedNetworks}
                  onChange={(v) => setForm({ ...form, allowedNetworks: v })}
                />
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Adding...' : 'Add plan'}</button>
                <button type="button" className="btn btn-outline" onClick={() => setShowAdd(false)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {editing && (
        <div className="modal-backdrop" onClick={() => setEditing(null)}>
          <div className="modal-card" style={{ maxWidth: 520 }} onClick={(e) => e.stopPropagation()}>
            <h3>Edit plan</h3>
            <form onSubmit={handleUpdate}>
              <div className="form-row">
                <div className="form-group"><label>Name</label>
                  <input required value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} /></div>
                <div className="form-group"><label>Price ($)</label>
                  <input type="number" min="0" required value={editing.price} onChange={(e) => setEditing({ ...editing, price: e.target.value })} /></div>
              </div>
              <div className="form-row">
                <div className="form-group"><label>Monthly credits</label>
                  <input type="number" min="0" required value={editing.monthlyCredits} onChange={(e) => setEditing({ ...editing, monthlyCredits: e.target.value })} /></div>
                <div className="form-group"><label>Daily cap</label>
                  <input type="number" min="0" required value={editing.dailyCreditCap} onChange={(e) => setEditing({ ...editing, dailyCreditCap: e.target.value })} /></div>
                <div className="form-group"><label>RPS</label>
                  <input type="number" min="1" required value={editing.rateLimitPerSecond} onChange={(e) => setEditing({ ...editing, rateLimitPerSecond: e.target.value })} /></div>
                <div className="form-group"><label>Max RPC URLs</label>
                  <input type="number" min="1" required value={editing.maxApiKeys} onChange={(e) => setEditing({ ...editing, maxApiKeys: e.target.value })} /></div>
              </div>
              <div className="form-group" style={{ marginBottom: 20 }}>
                <label>Allowed networks</label>
                <NetworkCheckboxes
                  networks={networks}
                  selected={editing.allowedNetworks}
                  onChange={(v) => setEditing({ ...editing, allowedNetworks: v })}
                />
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="submit" className="btn btn-primary" disabled={saving}>Save</button>
                <button type="button" className="btn btn-outline" onClick={() => setEditing(null)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
